import { DOCUMENT } from '@angular/common';
import { Inject, Injectable, Renderer2, RendererFactory2 } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { MemberQuery } from '../../../account/member/member.query';
import { APP_FEATURE, AppFeature } from '../../feature.token';

@Injectable({
  providedIn: 'root',
})
export class ThemeService {
  public isDarkMode: boolean = false;
  private renderer: Renderer2;
  private preferBrowserSettings: boolean = false;

  constructor(
    @Inject(DOCUMENT) private document: Document,
    @Inject(APP_FEATURE) private appFeature: AppFeature,
    private rendererFactory: RendererFactory2,
    private memberQuery: MemberQuery
  ) {
    this.renderer = this.rendererFactory.createRenderer(null, null);
  }

  init(): void {
    this.memberQuery.userContext$.pipe(takeUntilDestroyed()).subscribe((userContext) => {
      this.preferBrowserSettings = !!localStorage.getItem('bsTheme');

      if (this.preferBrowserSettings) {
        // If user has set a preference, use that
        this.isDarkMode = localStorage.getItem('bsTheme') === 'dark';
        this.applyModePreference();
        return;
      }

      // If user has a client attribute set, use that to determine theme
      // Client Attribute is set in sso ie. https://www.club-name.com/sso/login?ClientAttribute=Dark
      if (userContext?.clientAttribute) {
        this.isDarkMode = userContext.clientAttribute.toLowerCase() === 'dark';
      } else {
        // If feature is enabled, allow dark mode based on system preference
        if (this.appFeature.enableDarkMode) {
          // Fallback to system preference
          this.isDarkMode = window.matchMedia('(prefers-color-scheme: dark)').matches;
        }
      }
      this.applyModePreference();
    });

    // Allow dynamic switching based on system preference if feature is enabled, that means user can change the theme by changing system settings.
    // ie. if user's OS changes to dark mode, the app will also switch to dark mode
    if (this.appFeature.enableDarkMode) {
      // Listen to system preference changes
      window.matchMedia('(prefers-color-scheme: dark)').addEventListener('change', (e) => {
        this.isDarkMode = e.matches;
        this.applyModePreference();
      });
    }
  }

  private applyModePreference() {
    const mode = this.isDarkMode ? 'dark' : 'light';
    this.renderer.setAttribute(this.document.documentElement, 'data-bs-theme', mode);
  }

  /* Toggle dark mode manually, if user toggles the theme manually use browser settings. And will use that setting as preference and override any mode */
  toggleDarkMode(): void {
    this.isDarkMode = !this.isDarkMode;
    localStorage.setItem('bsTheme', this.isDarkMode ? 'dark' : 'light');
    this.applyModePreference();
  }

  /* Remove saved preference, will use system preference or client attribute from user context */
  removeSavedPreference(): void {
    localStorage.removeItem('bsTheme');
  }
}
